import { useState, useEffect, useRef, useCallback } from 'react';
import { API_URL } from '@/config';

const MAX_NOTIFICATIONS = 50;

/**
 * Connects to the backend WebSocket for live admin notifications.
 * Handles auto-reconnect with backoff and exposes the notification feed,
 * unread count and connection status.
 */
export function useRealtimeNotifications() {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [connected, setConnected] = useState(false);
  const [lastEvent, setLastEvent] = useState(null);

  const wsRef = useRef(null);
  const reconnectRef = useRef(null);
  const attemptsRef = useRef(0);
  const shouldReconnectRef = useRef(true);

  const buildWsUrl = useCallback(() => {
    const token = localStorage.getItem('session_token');
    if (!token) return null;
    const wsBase = API_URL.replace(/^http/, 'ws');
    return `${wsBase}/api/ws?token=${encodeURIComponent(token)}`;
  }, []);

  const connect = useCallback(() => {
    const url = buildWsUrl();
    if (!url) return;

    // Avoid opening a second socket if one is already active/connecting
    if (wsRef.current && (wsRef.current.readyState === WebSocket.OPEN || wsRef.current.readyState === WebSocket.CONNECTING)) {
      return;
    }

    let ws;
    try {
      ws = new WebSocket(url);
    } catch (e) {
      return;
    }
    ws._intentionalClose = false;
    wsRef.current = ws;

    ws.onopen = () => {
      setConnected(true);
      attemptsRef.current = 0;
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.type === 'connected') {
          return;
        }
        const notification = {
          id: `${data.type}-${data.timestamp}-${Math.random().toString(36).slice(2, 7)}`,
          type: data.type,
          payload: data.payload || {},
          timestamp: data.timestamp,
          read: false,
        };
        setNotifications((prev) => [notification, ...prev].slice(0, MAX_NOTIFICATIONS));
        setUnreadCount((prev) => prev + 1);
        setLastEvent(notification);
      } catch (e) {
        // ignore malformed messages
      }
    };

    ws.onclose = () => {
      // Only the currently-active, non-intentional socket may trigger reconnect
      if (wsRef.current === ws) {
        setConnected(false);
        wsRef.current = null;
      }
      if (shouldReconnectRef.current && !ws._intentionalClose) {
        const delay = Math.min(30000, 1000 * Math.pow(2, attemptsRef.current));
        attemptsRef.current += 1;
        reconnectRef.current = setTimeout(connect, delay);
      }
    };

    ws.onerror = () => {
      // let onclose handle reconnection
      try { ws.close(); } catch (e) { /* noop */ }
    };
  }, [buildWsUrl]);

  useEffect(() => {
    shouldReconnectRef.current = true;
    connect();
    return () => {
      shouldReconnectRef.current = false;
      if (reconnectRef.current) clearTimeout(reconnectRef.current);
      if (wsRef.current) {
        wsRef.current._intentionalClose = true;
        try { wsRef.current.close(); } catch (e) { /* noop */ }
        wsRef.current = null;
      }
    };
  }, [connect]);

  const markAllRead = useCallback(() => {
    setUnreadCount(0);
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
  }, []);

  const clearAll = useCallback(() => {
    setNotifications([]);
    setUnreadCount(0);
  }, []);

  return { notifications, unreadCount, connected, lastEvent, markAllRead, clearAll };
}
